import React, { useState } from 'react';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { useTheme, alpha } from '@mui/material/styles';
import { Copy, Check } from 'react-feather';
import { useSnackbar } from 'notistack';
import { copyAsCSV, markdownToPlainText } from '../../../utils/utils';
import { CONSTANTS } from '../../../constants/constants';

const removeNullUndefined = obj => {
  const cleaned = { ...obj };
  Object.keys(cleaned).forEach(key => {
    if (cleaned[key] === undefined || cleaned[key] === null) {
      delete cleaned[key];
    }
  });
  return cleaned;
};

export default function CopyButton({ message }) {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      // If message has table data (components, vulnerabilities), use copyAsCSV
      const hasComponents = message.components && message.components.length > 0;
      const hasVulnerabilities = message.vulnerabilities && message.vulnerabilities.length > 0;
      const hasTableData = hasComponents || hasVulnerabilities;

      if (hasTableData) {
        // Determine which table data to copy
        // Priority: components first, then vulnerabilities
        // If both exist, copy components (can be extended to handle multiple tables)
        let dataToCopy = [];

        if (hasComponents) {
          // Filter to only visible columns from SBOM table
          dataToCopy = message.components.map(comp => {
            const typeValue = comp.type || '';
            const normalizedType = typeValue.replace(/^COMPONENT_TYPE_/, '');

            const licenseArray = comp.licenses || [];
            const license = Array.isArray(licenseArray) ? licenseArray.join(', ') : licenseArray;
            const packageType = comp.packageType || comp.package?.type || null;

            const filtered = {
              name: comp.name,
              version: comp.version,
              vendor: comp.vendor,
              type: normalizedType,
              packageType: packageType,
              dependencies: comp.dependencies,
              license: license,
              confidence: comp.confidence,
              correlations: comp.correlations,
              vulnerability: comp.vulnerability,
              exploit: comp.exploit
            };
            return removeNullUndefined(filtered);
          });
        } else if (hasVulnerabilities) {
          // Filter to only visible columns from Vulnerabilities table
          dataToCopy = message.vulnerabilities.map(vuln => {
            const filtered = {
              cve: vuln.vulnerabilityName || vuln.cve || vuln.name,
              severity: vuln.severity,
              name: vuln.name,
              version: vuln.version,
              epssPercentile: vuln.epssPercentile,
              vendor: vuln.vendor,
              maturity: vuln.maturity,
              correlations: vuln.correlations || vuln.exploits || 0,
              vulnerabilityRemediationStatus: vuln.currentRemediation?.status || vuln.vexStatus
            };
            return removeNullUndefined(filtered);
          });
        }

        // Copy as CSV if we have table data
        if (dataToCopy.length > 0) {
          await copyAsCSV(dataToCopy);
          enqueueSnackbar(CONSTANTS.COPIED_TO_CLIPBOARD_MSG, { variant: 'success' });
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
          return;
        }
      }

      // Fallback to text copy for text-only messages
      let textToCopy = '';
      if (message.content) {
        // Convert markdown to plain text before copying
        textToCopy = markdownToPlainText(message.content);
      }

      // Add components info if present (fallback text format)
      if (hasComponents) {
        textToCopy += `\n\nComponents (${message.components.length}):\n`;
        message.components.forEach(comp => {
          textToCopy += `- ${comp.name} ${comp.version} (${comp.vendor || 'Unknown vendor'})\n`;
        });
      }

      // Add vulnerabilities info if present (fallback text format)
      if (hasVulnerabilities) {
        textToCopy += `\n\nVulnerabilities (${message.vulnerabilities.length}):\n`;
        message.vulnerabilities.forEach(vuln => {
          const name = vuln.vulnerabilityName || vuln.cve || vuln.name || 'Unknown';
          textToCopy += `- ${name}\n`;
        });
      }

      if (textToCopy.trim()) {
        await navigator.clipboard.writeText(textToCopy.trim());
        enqueueSnackbar(CONSTANTS.COPIED_TO_CLIPBOARD_MSG, { variant: 'success' });
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (err) {
      enqueueSnackbar('Failed to copy to clipboard.', { variant: 'error' });
    }
  };

  return (
    <Tooltip title={copied ? 'Copied!' : 'Copy'}>
      <IconButton
        size="small"
        onClick={handleCopy}
        sx={{
          p: theme.spacing(0.25),
          color: copied ? theme.palette.success.main : theme.palette.text.secondary,
          '&:hover': {
            backgroundColor: alpha(theme.palette.text.secondary, 0.1),
            color: theme.palette.text.secondary
          }
        }}
      >
        {copied ? <Check size={20} /> : <Copy size={20} />}
      </IconButton>
    </Tooltip>
  );
}
